import { normalizeReportData } from './reportDataMerge.js';
import { retainCountryCurrency } from './countryCurrency.js';

const indicators = [
    ['gdpGrowth', 'gdpGrowth', 'GDP growth', true],
    ['inflation', 'inflation', 'Inflation', true],
    ['unemployment', 'unemployment', 'Unemployment', true],
    ['debt_to_gdp', 'debtToGdp', 'Debt to GDP', true],
    ['gni_per_capita', 'gniPerCapita', 'GNI per capita', false],
    ['foreignReserves', 'foreignReserves', 'Foreign reserves', false],
] as const;
const gapPrefix = 'Economic intelligence missing: ';

export function economicValue(value: unknown): string | null {
    if (typeof value === 'number')
        return Number.isFinite(value) ? String(value) : null;
    if (typeof value !== 'string')
        return null;
    const text = value.trim();
    return text && !/^(?:\$?N\/A(?:%|\s|$)|USE VALUE FROM STRUCTURED DATA|undefined\b|null\b|NaN\b|Data unavailable|Economic context unavailable|Unknown\b|Not available|Not reported|No data|Insufficient current intelligence|\.\.\.)/i.test(text) ? text : null;
}

function numeric(value: unknown, percent: boolean): number | null {
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    const text = economicValue(value)?.replace(/,/g, '').replace(percent ? /%$/ : /^\$|\s*USD$/gi, '').trim();
    return text && /^[-+]?\d+(?:\.\d+)?$/.test(text) ? Number(text) : null;
}

// Only economic fields cross from the saved national report into a fresh facility report.
export function savedCountryEconomics(baseline: any): any {
    const saved = normalizeReportData(baseline) || {};
    return { economicIndicators: saved.economicIndicators || {}, worldBank: saved.quantitativeIndicators?.worldBank || {} };
}

export function economicIntelligencePrompt(baseline: any): string {
    return `ECONOMIC INTELLIGENCE:\nSaved country economic data: ${JSON.stringify(savedCountryEconomics(baseline))}
Report GDP growth, inflation, unemployment, debt to GDP, GNI per capita, foreign reserves, and currency in economicIndicators.
Use supplied structured figures before narrative estimates. Preserve supplied reference periods; annual statistics are not today's observations.
Do not infer a statistical reference year from the report date or retrieval time. If no reference year is supplied, say it is unavailable.
Write a sourced economic summary covering current economic conditions and their effects on this facility, staffing, costs, supply chains, and operations.
Distinguish current reporting from historical figures. Leave unavailable figures as Data unavailable; never estimate missing numbers.`;
}

export function enrichEconomicIntelligence(report: any, baseline?: any, countryCode?: string): any {
    const saved = savedCountryEconomics(baseline);
    const economic = { ...(report?.economicIndicators || {}) };
    let worldBank = { ...(report?.quantitativeIndicators?.worldBank || {}) };
    const indicatorYears = { ...(worldBank.indicatorYears || {}) };
    const missing: string[] = [];
    let usedSavedFigures = false;
    for (let index = 0; index < indicators.length; index++) {
        const [bankKey, economicKey, label, percent] = indicators[index];
        const savedNumber = numeric(saved.worldBank[bankKey], percent);
        const currentNumber = numeric(worldBank[bankKey], percent);
        const savedText = economicValue(saved.economicIndicators[economicKey] ?? saved.economicIndicators[bankKey]);
        const currentText = economicValue(economic[economicKey] ?? economic[bankKey]);
        const savedFigure = savedNumber ?? numeric(savedText, percent);
        const selected = savedFigure ?? currentNumber ?? numeric(currentText, percent);
        if (selected !== null) {
            worldBank[bankKey] = selected;
            const source = savedFigure !== null ? saved.worldBank : worldBank;
            indicatorYears[bankKey] = economicValue(source.indicatorYears?.[bankKey] ?? source.dataYear) || '';
            economic[economicKey] = percent ? `${selected}%` : new Intl.NumberFormat('en-US', {
                style: 'currency', currency: 'USD', maximumFractionDigits: 2,
            }).format(selected);
        } else {
            delete worldBank[bankKey];
            economic[economicKey] = savedText || currentText || 'Data unavailable';
            if (!savedText && !currentText)
                missing.push(label);
        }
        if (savedNumber !== null || savedText)
            usedSavedFigures = true;
    }
    // Keep other available economic statistics and their source metadata for exports.
    for (const key of Object.keys(saved.worldBank))
        if (key !== 'indicatorYears' && !indicators.some(([bankKey]) => bankKey === key) && saved.worldBank[key] !== null && saved.worldBank[key] !== undefined)
            worldBank[key] = saved.worldBank[key];
    worldBank.indicatorYears = { ...saved.worldBank.indicatorYears, ...indicatorYears };
    if (usedSavedFigures && !saved.worldBank.dataYear)
        delete worldBank.dataYear;
    const currency = retainCountryCurrency(worldBank, economic, baseline, countryCode);
    worldBank = currency.worldBank;
    const context = report?.pmesii?.economic;
    const summary = economicValue(economic.summary) || economicValue(economic.worldBankDataContext) ||
        economicValue(typeof context === 'string' ? context : context?.description || context?.summary);
    const dataGaps = (Array.isArray(report?.dataGaps) ? report.dataGaps : [])
        .filter((gap: unknown) => typeof gap !== 'string' || !gap.startsWith(gapPrefix));
    if (missing.length)
        dataGaps.push(`${gapPrefix}${missing.join(', ')}. Retry this analysis when country data is available.`);
    return { ...report, economicIndicators: { ...currency.economicIndicators,
        summary: summary || 'Economic context unavailable. Retry this analysis.' },
        quantitativeIndicators: { ...report?.quantitativeIndicators, worldBank }, dataGaps };
}
